import { useState, useEffect } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import api from '../../utils/api'

interface Notice {
  id: number
  title: string
  content: string
  is_active: number
  created_at: string
  updated_at: string
}

const AdminNotices = () => {
  const [notices, setNotices] = useState<Notice[]>([])
  const [loading, setLoading] = useState(true)
  const [editingNotice, setEditingNotice] = useState<Notice | null>(null)
  const [showForm, setShowForm] = useState(false)
  
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    loadNotices()
  }, [])

  const loadNotices = async () => {
    try {
      const response = await api.get('/notices/all')
      setNotices(response.data)
    } catch (error) {
      console.error('공지사항 로드 오류:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setMessage('')

    if (!title.trim() || !content.trim()) {
      setError('제목과 내용을 입력해주세요.')
      return
    }

    try {
      if (editingNotice) {
        await api.put(`/notices/${editingNotice.id}`, {
          title,
          content,
          is_active: isActive ? 1 : 0
        })
        setMessage('공지사항이 수정되었습니다.')
      } else {
        await api.post('/notices', {
          title,
          content,
          is_active: isActive ? 1 : 0
        })
        setMessage('공지사항이 생성되었습니다.')
      }
      
      resetForm()
      loadNotices()
    } catch (err: any) {
      setError(err.response?.data?.error || '작업에 실패했습니다.')
    }
  }

  const handleEdit = (notice: Notice) => {
    setEditingNotice(notice)
    setTitle(notice.title)
    setContent(notice.content)
    setIsActive(notice.is_active === 1)
    setShowForm(true)
  }

  const handleDelete = async (id: number) => {
    if (!confirm('정말 삭제하시겠습니까?')) return

    try {
      await api.delete(`/notices/${id}`)
      setMessage('공지사항이 삭제되었습니다.')
      loadNotices()
    } catch (err: any) {
      setError(err.response?.data?.error || '삭제에 실패했습니다.')
    }
  }

  const resetForm = () => {
    setEditingNotice(null)
    setTitle('')
    setContent('')
    setIsActive(true)
    setShowForm(false)
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="loading">로딩 중...</div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>공지사항 관리</h1>
        {!showForm && (
          <button 
            className="btn btn-primary"
            onClick={() => setShowForm(true)}
          >
            ➕ 새 공지사항
          </button>
        )}
      </div>

      {message && (
        <div className="alert alert-success" style={{ marginBottom: '20px' }}>
          {message}
        </div>
      )}

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {showForm && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h2>{editingNotice ? '공지사항 수정' : '새 공지사항 작성'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>제목</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="공지사항 제목"
                required
              />
            </div>

            <div className="form-group">
              <label>내용</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="공지사항 내용을 입력하세요"
                rows={10}
                required
                style={{ resize: 'vertical' }}
              />
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  style={{ width: 'auto' }}
                />
                활성화 (체크하면 홈페이지에 표시됩니다)
              </label>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" className="btn btn-primary">
                {editingNotice ? '수정' : '등록'}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={resetForm}
              >
                취소
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <h2>공지사항 목록</h2>
        {notices.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#666', padding: '30px' }}>
            등록된 공지사항이 없습니다.
          </p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: '80px' }}>상태</th>
                <th>제목</th>
                <th style={{ width: '150px' }}>작성일</th>
                <th style={{ width: '150px' }}>수정일</th>
                <th style={{ width: '150px' }}>작업</th>
              </tr>
            </thead>
            <tbody>
              {notices.map(notice => (
                <tr key={notice.id}>
                  <td>
                    <span 
                      className={`btn ${notice.is_active ? 'btn-success' : 'btn-secondary'}`}
                      style={{ padding: '4px 8px', fontSize: '12px' }}
                    >
                      {notice.is_active ? '활성' : '비활성'}
                    </span>
                  </td>
                  <td>
                    <strong>{notice.title}</strong>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      {notice.content.substring(0, 100)}
                      {notice.content.length > 100 ? '...' : ''}
                    </div>
                  </td>
                  <td>{new Date(notice.created_at).toLocaleDateString('ko-KR')}</td>
                  <td>{new Date(notice.updated_at).toLocaleDateString('ko-KR')}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button
                        onClick={() => handleEdit(notice)}
                        className="btn btn-primary"
                        style={{ padding: '5px 10px', fontSize: '12px' }}
                      >
                        수정
                      </button>
                      <button
                        onClick={() => handleDelete(notice.id)}
                        className="btn btn-danger"
                        style={{ padding: '5px 10px', fontSize: '12px' }}
                      >
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  )
}

export default AdminNotices




